import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { invoiceService, type InvoiceSummary, type InvoiceListResult } from '../services/invoiceService';
import { formatInvoiceDate } from './invoiceDate';

const money = (value: string | undefined, currency: string) => `${currency} ${Number(value || 0).toFixed(2)}`;

export const InvoiceListPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [result, setResult] = useState<InvoiceListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState(query.get('q') || '');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    invoiceService.list(query).then((data) => {
      if (current) setResult(data);
    }).catch((reason) => {
      if (current) setError(reason instanceof Error ? reason.message : 'Could not load invoices');
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [query, reload]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(query);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('page');
    navigate(`?${next.toString()}`);
  };
  const setPage = (value: number) => {
    const next = new URLSearchParams(query);
    next.set('page', String(value));
    navigate(`?${next.toString()}`);
  };

  const invoices: InvoiceSummary[] = result?.items ?? [];
  const page = Number(query.get('page') || '1');
  const pageSize = result?.page_size || 20;

  return <Box data-testid="invoice-list">
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} mb={3}>
      <Typography variant="body1" color="text.secondary">Find drafts and issued invoices. Payment, email and fulfilment are tracked separately.</Typography>
      <Button component={RouterLink} to="/madebb/invoices/new" variant="contained">New invoice</Button>
    </Stack>
    <Stack component="form" direction={{ xs: 'column', md: 'row' }} spacing={2} mb={3} onSubmit={(event) => { event.preventDefault(); setFilter('q', search.trim()); }}>
      <TextField label="Invoice number or customer" value={search} onChange={(event) => setSearch(event.target.value)} size="small" />
      <Button type="submit" variant="outlined">Search</Button>
      <TextField select label="Currency" value={query.get('currency') || ''} onChange={(event) => setFilter('currency', event.target.value)} size="small" sx={{ minWidth: 130 }}>
        <MenuItem value="">All currencies</MenuItem><MenuItem value="BBD">BBD</MenuItem><MenuItem value="USD">USD</MenuItem>
      </TextField>
      <TextField select label="Payment status" value={query.get('status') || ''} onChange={(event) => setFilter('status', event.target.value)} size="small" sx={{ minWidth: 170 }}>
        <MenuItem value="">All statuses</MenuItem>
        {['draft', 'issued', 'part_paid', 'paid', 'payment_exception', 'voided'].map((status) => <MenuItem key={status} value={status}>{status.replaceAll('_', ' ')}</MenuItem>)}
      </TextField>
      <TextField label="From date" type="date" InputLabelProps={{ shrink: true }} value={query.get('from_date') || ''} onChange={(event) => setFilter('from_date', event.target.value)} size="small" />
      <TextField label="To date" type="date" InputLabelProps={{ shrink: true }} value={query.get('to_date') || ''} onChange={(event) => setFilter('to_date', event.target.value)} size="small" />
    </Stack>
    {loading && <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><span>Loading invoices…</span></Stack>}
    {error && <Alert severity="error" action={<Button color="inherit" onClick={() => setReload((value) => value + 1)}>Retry</Button>}>{error}</Alert>}
    {!loading && !error && invoices.length === 0 && <Alert severity="info">No invoices match these filters.</Alert>}
    {!loading && !error && invoices.length > 0 && <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label="Invoices">
        <TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Customer</TableCell><TableCell>Seller</TableCell><TableCell>Issued / due</TableCell><TableCell>Total</TableCell><TableCell>Balance</TableCell><TableCell>Payment</TableCell><TableCell>Email</TableCell><TableCell>Fulfilment</TableCell></TableRow></TableHead>
        <TableBody>{invoices.map((invoice) => <TableRow key={invoice.id} hover>
          <TableCell><Button component={RouterLink} to={`/madebb/invoices/${encodeURIComponent(invoice.id)}${location.search}`} size="small">{invoice.number || 'Draft'}</Button></TableCell>
          <TableCell>{invoice.customer?.company || invoice.customer?.name || invoice.customer_name || '—'}</TableCell>
          <TableCell>{invoice.seller_snapshot?.name || '—'}</TableCell>
          <TableCell>{formatInvoiceDate(invoice.issue_date)} / {formatInvoiceDate(invoice.due_date, true)}</TableCell>
          <TableCell>{money(invoice.totals?.grand_total, invoice.currency)}</TableCell>
          <TableCell>{money(invoice.totals?.balance ?? (Number(invoice.totals?.grand_total || 0) - Number(invoice.totals?.paid || 0)).toFixed(2), invoice.currency)}</TableCell>
          <TableCell>{invoice.status.replaceAll('_', ' ')}{invoice.paid_at && <Typography variant="caption" display="block">{formatInvoiceDate(invoice.paid_at)}</Typography>}</TableCell>
          <TableCell>{invoice.email_status || 'Not sent'}</TableCell>
          <TableCell>{invoice.fulfilment_status || 'Not available'}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </TableContainer>}
    {!loading && !error && (page > 1 || (result?.total || 0) > page * pageSize) && <Stack direction="row" justifyContent="flex-end" spacing={1} mt={2}>
      <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
      <Typography alignSelf="center">Page {page}</Typography>
      <Button disabled={(result?.total || 0) <= page * pageSize} onClick={() => setPage(page + 1)}>Next</Button>
    </Stack>}
  </Box>;
};
